import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { TodoListResponse } from '@/lib/api';

interface ListSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  list: TodoListResponse;
  onSave: (input: { isPublic?: boolean; publicSlug?: string }) => Promise<void> | void;
}

export function ListSettingsDialog({ open, onClose, list, onSave }: ListSettingsDialogProps) {
  const [isPublic, setIsPublic] = useState(list.isPublic);
  const [publicSlug, setPublicSlug] = useState(list.publicSlug ?? '');

  useEffect(() => {
    if (open) {
      setIsPublic(list.isPublic);
      setPublicSlug(list.publicSlug ?? '');
      setSaveError(null);
    }
  }, [open, list]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      onClose();
    }
  }

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        isPublic,
        publicSlug: isPublic ? publicSlug.trim() || undefined : undefined,
      });
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>List Settings</DialogTitle>
          <DialogDescription>Configure visibility and sharing for this list.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="public-toggle">Make Public</Label>
              <p className="text-xs text-muted-foreground">
                Allow anyone with the link to view this list
              </p>
            </div>
            <Switch id="public-toggle" checked={isPublic} onCheckedChange={setIsPublic} />
          </div>

          {isPublic && (
            <div className="space-y-2">
              <Label htmlFor="public-slug">Public Slug</Label>
              <Input
                id="public-slug"
                value={publicSlug}
                onChange={(e) => setPublicSlug(e.target.value.toLowerCase())}
                placeholder="my-todo-list"
              />
              {publicSlug.trim() && (
                <p className="text-xs text-muted-foreground">
                  Preview:{' '}
                  <code className="rounded bg-muted px-1 py-0.5">
                    /a/arche_todo_lists/public/{publicSlug.trim()}
                  </code>
                </p>
              )}
            </div>
          )}

          {saveError && <p className="text-sm text-destructive">{saveError}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
